import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import FormatValue from 'assets/components/formatValue/FormatValue';
import Icon from 'assets/components/icon/Icon';
import ThemeTableSimple from 'assets/components/table/themes/Theme.Table.Simple';

import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import { formatCurrency } from 'assets/utils/data/Number';
import ContractRefund from 'models/sales/contractRefund/Model.ContractRefund';
import { useState } from 'react';
import { ExcludedAmount } from './ExcludedAmount';

const nestedTableCellStyle = { padding: 0, textAlign: 'left' } as const;
const refundAmountStyle = { textAlign: 'center', fontWeight: 'bold' } as const;
const customerRefundStyle = { textAlign: 'center', fontWeight: 'bold', color: Theme.Color.text } as const;

const SummaryTableColumns = () => (
  <colgroup>
    <col style={{ width: '40%' }} />
    <col style={{ width: '20%' }} />
    <col style={{ width: '20%' }} />
    <col style={{ width: '20%' }} />
  </colgroup>
);

export const ContractRefundCostControlsSummaryTable = ({ contractRefund }: { contractRefund?: ContractRefund }) => {
  const { lang } = getLocales();
  const [isDeductionBreakdownOpen, setIsDeductionBreakdownOpen] = useState<boolean>(false);

  const deductionBreakdown = [
    { label: lang.administrationFee, value: contractRefund?.totalDeductions },
    { label: lang.administrationFeeTax, value: contractRefund?.totalDeductionsTaxAmount },
    {
      label: lang.sumOfClaimPreTaxAmountsPaidOnContract,
      value: contractRefund?.sumOfClaimAmountsPaidOnContract ?? 0,
    },
    {
      label: lang.sumOfClaimGstHstAmountsPaidOnContract,
      value: contractRefund?.sumOfClaimGstHstAmountsPaidOnContract ?? 0,
    },
    {
      label: lang.sumOfClaimPstAmountsPaidOnContract,
      value: contractRefund?.sumOfClaimProvincialSalesTaxAmountsPaidOnContract ?? 0,
    },
  ];

  return (
    <table className={ThemeTableSimple}>
      <SummaryTableColumns />
      <thead>
        <tr>
          <th>{lang.contractSummaryAmounts}</th>
          <th style={{ textAlign: 'center' }}>{lang.valueAtSales}</th>
          <th style={{ textAlign: 'center' }}>{lang.valueRetained}</th>
          <th style={{ textAlign: 'center' }}>{lang.refundAmount}</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>{lang.dealerCostLessAllDeductions}</td>
          <td style={{ textAlign: 'center' }}>
            <FormatValue value={formatCurrency(contractRefund?.dealerCostValueAtSale)} />
          </td>
          <td style={{ textAlign: 'center', display: 'flex', justifyContent: 'space-evenly', alignItems: 'center' }}>
            <FormatValue value={formatCurrency(contractRefund?.retainedDealerCostTotalPlusDeductions)} />
            <Button
              class={ThemeButtonCircle}
              onClick={() => setIsDeductionBreakdownOpen((isOpen) => !isOpen)}
              media={<Icon class={isDeductionBreakdownOpen ? 'fas-caret-up' : 'fas-caret-down'} />}
              htmlElementProps={{
                title: isDeductionBreakdownOpen ? lang.hideDeductionBreakdown : lang.showDeductionBreakdown,
              }}
            />
          </td>
          <td style={refundAmountStyle}>
            <FormatValue value={formatCurrency(contractRefund?.refundedDealerCostTotalLessDeductions)} />
          </td>
        </tr>
        {isDeductionBreakdownOpen &&
          deductionBreakdown.map(({ label, value }) => (
            <tr key={label}>
              <td style={{ paddingLeft: Theme.Size.L, fontWeight: 'normal' }}>{label}</td>
              <td />
              <td style={{ textAlign: 'center', fontWeight: 'normal' }}>
                <FormatValue value={formatCurrency(value)} />
              </td>
              <td />
            </tr>
          ))}
        <tr>
          <td>{lang.ascDealerProfitResponsibility}</td>
          <td style={{ textAlign: 'center' }}>
            <FormatValue value={formatCurrency(contractRefund?.dealerProfitValueAtSale)} />
          </td>
          <td style={{ textAlign: 'center' }}>
            <FormatValue value={formatCurrency(contractRefund?.retainedASCDealerProfitTotal)} />
          </td>
          <td style={refundAmountStyle}>
            <ExcludedAmount isExcluded={contractRefund?.isRefundedASCDealerProfitTotalExcluded}>
              <FormatValue value={formatCurrency(contractRefund?.refundedASCDealerProfitTotal)} />
            </ExcludedAmount>
          </td>
        </tr>

        <tr>
          <td colSpan={4} style={nestedTableCellStyle}>
            <table className={ThemeTableSimple}>
              <SummaryTableColumns />
              <thead>
                <tr>
                  <th colSpan={3}>{lang.totalToBeRefundedToCustomer}</th>
                  <th style={customerRefundStyle}>
                    <FormatValue value={formatCurrency(contractRefund?.totalToBeRefunded)} />
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{lang.dealerProfitProtectedHeldBackForRecovery}</td>
                  <td style={{ textAlign: 'center' }}>
                    <FormatValue value={formatCurrency(contractRefund?.dealerProfitValueAtSale)} />
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <FormatValue value={formatCurrency(contractRefund?.retainedDealerProfitTotal)} />
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <ExcludedAmount isExcluded={contractRefund?.isRefundedDealerProfitTotalExcluded}>
                      <FormatValue value={formatCurrency(contractRefund?.refundedDealerProfitTotal)} />
                    </ExcludedAmount>
                  </td>
                </tr>
              </tbody>
            </table>
          </td>
        </tr>

        <tr>
          <td colSpan={4} style={nestedTableCellStyle}>
            <table className={ThemeTableSimple}>
              <SummaryTableColumns />
              <thead>
                <tr>
                  <th>{lang.contractSummaryAmounts}</th>
                  <th style={{ textAlign: 'center' }}>{lang.valueAtSales}</th>
                  <th style={{ textAlign: 'center' }}>{lang.valueRetained}</th>
                  <th style={{ textAlign: 'center' }}>{lang.refundAmount}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td colSpan={3}>{lang.recoverableFromDealerNotPartOfCustomerRefund}</td>
                  <td style={{ textAlign: 'center' }}>
                    <ExcludedAmount isExcluded={contractRefund?.isRefundedDealerProfitTotalExcluded}>
                      <FormatValue value={formatCurrency(contractRefund?.refundedDealerProfitTotal)} />
                    </ExcludedAmount>
                  </td>
                </tr>
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  );
};
